import {type FC, type ReactNode, useEffect, useState} from "react";

import mapXIpApi from "api/ipApi/mapxIpApi";
import AuthView from "components/AuthView";
import LogoAnimation from "components/LogoAnimation";
import {isSanctionedLocation} from "helpers/geoBlocking";
import {Mixpanel} from "helpers/mixpanel";

// The lookup holds back the first render, so a slow ipapi.co must not hang the app.
const LOOKUP_TIMEOUT_MS = 3000;

type TGeoStatus = "checking" | "allowed" | "blocked";

const isLocalhost = () => window.location.hostname === "localhost";

const GeoBlockGate: FC<{children: ReactNode}> = ({children}) => {
	const [status, setStatus] = useState<TGeoStatus>(() =>
		isLocalhost() ? "allowed" : "checking",
	);

	useEffect(() => {
		if (isLocalhost()) return;

		let cancelled = false;

		mapXIpApi
			.getLocation({timeout: LOOKUP_TIMEOUT_MS})
			.then(({data: location}) => {
				if (cancelled) return;

				if (isSanctionedLocation(location)) {
					Mixpanel.track("App: Region Blocked", {country: location.country});
					setStatus("blocked");
				} else {
					setStatus("allowed");
				}
			})
			// Fail open: this is a UI-level check, and an ad blocker or an ipapi.co
			// outage must not lock every user out of the app.
			.catch(() => {
				if (!cancelled) setStatus("allowed");
			});

		return () => {
			cancelled = true;
		};
	}, []);

	if (status === "checking") {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<LogoAnimation />
			</div>
		);
	}

	if (status === "blocked") {
		return (
			<AuthView
				error=""
				loading={false}
				title="MapX Not Available"
				description="Sorry, MapX isn't available in your region."
			/>
		);
	}

	return <>{children}</>;
};

export default GeoBlockGate;
